import { View, Text, hexColor } from "@lightningtv/solid";
import { createSignal, onMount, onCleanup, Show } from "solid-js";
import { fontFamily, isRTL, t } from "../translations/configuration";
import Header from "./Header";

export default function Termsconditions(props: {
    onPinSet: (pin?: string) => void;
    onAcceptTerms?: (accepted: boolean) => void;
}) {
    const [focused, setFocused] = createSignal(false);
    const [checked, setChecked] = createSignal(false);
    const [showError, setShowError] = createSignal(false);
    const [checkboxFocused, setCheckboxFocused] = createSignal(false);

    let checkboxRef, agreeRef;

    const termOne = `${t("one")}`;
    const termTwo = `${t("two")}`;
    const termThree = `${t("three")}`;
    const termFour = `${t("four")}`;
    const termFive = `${t("five")}`;
    const termSix = `${t("six")}`;

    const handleAccept = () => {
        if (!checked()) {
            setShowError(true);
            setTimeout(() => setShowError(false), 3000);
            agreeRef?.setFocus();
            return;
        }
        if (props.onAcceptTerms) {
            props.onAcceptTerms(true);
        }
        props.onPinSet?.();
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter' && focused()) {
            handleAccept();
        }
    };

    onMount(() => {
        document.addEventListener('keydown', handleKeyDown);
    });

    onCleanup(() => {
        document.removeEventListener('keydown', handleKeyDown);
    });

    return (
        <View color={hexColor("#0d0f14")} width={1920} height={1080}>

            <Header screen={""} />

            {/* Title */}
            <Text x={560} y={140} style={{ fontSize: 40, color: hexColor("#f0f6fc"), fontWeight: isRTL() ? "normal" : "bold", fontFamily: fontFamily() }}>
                OrbixPlay User Agreement and Disclaimer
            </Text>
            <Text x={630} y={196} style={{ fontSize: 26, color: hexColor("#8b949e"), fontFamily: fontFamily() }}>
                Please read carefully before proceeding
            </Text>

            {/* Terms card */}
            <View x={150} y={240} width={1600} height={560} borderRadius={20} color={hexColor("#161b22")}>
                <Text x={40} y={30} style={{ fontSize: 22, color: hexColor("#c9d1d9"), fontFamily: fontFamily() }}>
                    {t("beforeyouProcc")}
                </Text>

                {[termOne, termTwo, termThree, termFour, termFive, termSix].map((term, index) => {
                    const colonIndex = term.indexOf(":");
                    const titlePart = term.substring(0, colonIndex);
                    const bodyPart = term.substring(colonIndex + 1).trim();
                    const dotIndex = titlePart.indexOf(".");
                    const number = titlePart.substring(0, dotIndex + 1);
                    const titleText = titlePart.substring(dotIndex + 1).trim();
                    const Y = 80 + index * 80;

                    return (
                        <View x={60} y={Y}>
                            <Text style={{ fontSize: 20, color: hexColor("#2563eb"), fontWeight: isRTL() ? "normal" : "bold", fontFamily: fontFamily() }}>
                                {number}
                            </Text>
                            <Text x={36} style={{ fontSize: 20, color: hexColor("#f0f6fc"), fontWeight: isRTL() ? "normal" : "bold", fontFamily: fontFamily() }}>
                                {titleText}:
                            </Text>
                            <Text y={30} style={{ fontSize: 18, color: hexColor("#8b949e"), width: 1450, contain: "width", fontFamily: fontFamily() }}>
                                {bodyPart}
                            </Text>
                        </View>
                    );
                })}
            </View>

            {/* Error message */}
            <Show when={showError()}>
                <View x={620} y={820} width={680} height={44} borderRadius={10} color={hexColor("#ef444422")}>
                    <Text x={20} y={10} style={{ fontSize: 22, color: hexColor("#ef4444"), fontFamily: fontFamily() }}>
                        ⚠ Please accept the Terms and Conditions first
                    </Text>
                </View>
            </Show>

            {/* Checkbox row */}
            <View
                ref={el => { checkboxRef = el; }}
                x={650} y={870}
                width={740} height={52}
                borderRadius={10}
                focusable={true}
                onFocus={() => setCheckboxFocused(true)}
                onBlur={() => setCheckboxFocused(false)}
                onEnter={() => {
                    setChecked(c => !c);
                    setShowError(false);
                }}
                onDown={() => agreeRef?.setFocus()}
            >
                {/* Border wrapper — color switches on focus */}
                <View
                    x={0} y={0}
                    width={740} height={52}
                    borderRadius={10}
                >
                    {/* Inner background inset by 2px */}
                    <View
                        x={2} y={2}
                        width={736} height={48}
                        borderRadius={8}
                    >
                        {/* Checkbox box */}
                        <View x={10} y={6} width={36} height={36} borderRadius={6} color={hexColor(checkboxFocused() ? "#2563eb" : "#2d3748")}>
                            <View x={2} y={2} width={32} height={32} borderRadius={4}
                                color={hexColor(checked() ? "#2563eb" : "#0d1117")} />
                            <Show when={checked()}>
                                <Text x={10} y={8} style={{ fontSize: 24, color: hexColor("#ffffff"), fontWeight: isRTL() ? "normal" : "bold" }}>X</Text>
                            </Show>
                        </View>

                        <Text x={60} y={12} style={{
                            fontSize: 24,
                            color: hexColor(checkboxFocused() ? "#f0f6fc" : "#c9d1d9"),
                            fontStyle: "italic",
                            fontFamily: fontFamily(),
                        }}>
                            I have read and agree to the Terms and Conditions
                        </Text>
                    </View>
                </View>
            </View>

            {/* Agree button */}
            <View
                ref={el => { agreeRef = el; }}
                x={780} y={940}
                width={304} height={72}
                borderRadius={16}
                color={hexColor(focused() ? "#2563eb" : "#2d3748")}
                focusable={true}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                onUp={() => checkboxRef?.setFocus()}
                onEnter={handleAccept}
                autofocus={true}
            >
                {/* Border wrapper */}
                <View
                    x={0} y={0}
                    width={304} height={72}
                    borderRadius={16}
                    color={hexColor(focused() ? "#2563eb" : "#2d3748")}
                >
                    {/* Inner background */}
                    <View
                        x={2} y={2}
                        width={300} height={68}
                        borderRadius={14}
                        color={hexColor("#1e2330")}
                    >
                        <Text x={110} y={18} style={{
                            fontSize: 30,
                            color: hexColor("#ffffff"),
                            fontWeight: isRTL() ? "normal" : "bold",
                            fontFamily: fontFamily(),
                        }}>
                            {t('agree')} →
                        </Text>
                    </View>
                </View>
            </View>

        </View >
    );
}